import { ClientLoggingService } from '../shared/client-logging.service';
/**
 * Admin Guard - Route Protection for Administrative Console
 *
 * This functional guard protects the /admin route by verifying that the current user
 * has root-level administrative privileges. It performs the following checks:
 *
 * 1. Verifies user is authenticated via Firebase Auth
 * 2. Calls the backend RBAC service to check for 'root' admin role
 * 3. Redirects unauthorized users to appropriate error pages
 *
 * Only users with admin.roles.role: "root" in their Firebase Custom Claims
 * are granted access to the Administrative Console.
 *
 * @returns Observable<boolean> - true if user has admin access, false otherwise
 */

import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { map, catchError, switchMap, take, filter } from 'rxjs/operators';
import { FirebaseAuthService } from './firebase-auth.service';
import { RbacService } from './rbac.service';

export const adminGuard = () => {
  const firebaseAuthService = inject(FirebaseAuthService);
  const rbacService = inject(RbacService);
  const router = inject(Router);

  return firebaseAuthService.user$.pipe(
    filter(user => user !== null), // Wait until we have an actual user (not null or undefined)
    take(1),
    switchMap(user => {
      ClientLoggingService.log(
        'INFO',
        '[AdminGuard] DEBUG: User state received:',
        user ? `${user.email} (${user.uid})` : 'null'
      );

      if (!user) {
        ClientLoggingService.log('INFO', '[AdminGuard] DEBUG: No authenticated user, redirecting to unauthorized');
        router.navigate(['/unauthorized']);
        return of(false);
      }

      ClientLoggingService.log('INFO', `[AdminGuard] DEBUG: Authenticated user found: ${user.email}`);
      ClientLoggingService.log('INFO', `[AdminGuard] DEBUG: User UID: ${user.uid}`);

      // Check if user has admin role (specifically root role for admin UI access)
      ClientLoggingService.log('INFO', `[AdminGuard] DEBUG: Starting admin role check for root role...`);
      return rbacService.checkAdminRole('', 'root').pipe(
        map(response => {
          ClientLoggingService.log('INFO', `[AdminGuard] DEBUG: Received admin role check response:`, response);

          const hasAdminRole = response.hasAdminRole; // Use camelCase property name
          ClientLoggingService.log('INFO', `[AdminGuard] DEBUG: hasAdminRole = ${hasAdminRole}`);
          ClientLoggingService.log('INFO', `[AdminGuard] DEBUG: adminRoleFound = ${response.adminRoleFound}`); // Use camelCase property name

          if (!hasAdminRole) {
            ClientLoggingService.log(
              'INFO',
              '[AdminGuard] DEBUG: User does not have admin role, redirecting to unauthorized'
            );
            ClientLoggingService.log(
              'INFO',
              `[AdminGuard] DEBUG: Full response object:`,
              JSON.stringify(response, null, 2)
            );
            router.navigate(['/unauthorized'], {
              queryParams: {
                message: 'Admin privileges required to access this page',
              },
            });
            return false;
          }

          ClientLoggingService.log(
            'INFO',
            `[AdminGuard] DEBUG: SUCCESS! User ${user.email} has admin role: ${response.adminRoleFound}`
          );
          return true;
        }),
        catchError((error: any) => {
          ClientLoggingService.log('ERROR', '[AdminGuard] ERROR: Error checking admin role:', error);
          ClientLoggingService.log('ERROR', '[AdminGuard] ERROR: Error message:', error?.message);
          ClientLoggingService.log('ERROR', '[AdminGuard] ERROR: Error code:', error?.code);
          ClientLoggingService.log('ERROR', '[AdminGuard] ERROR: Error stack:', error?.stack);
          router.navigate(['/error'], {
            queryParams: {
              message: 'Error verifying admin privileges',
            },
          });
          return of(false);
        })
      );
    })
  );
};
